import type { ApiMessage } from '../api/types';

import { getMessageText } from '../global/helpers';
import { MAIN_IDB_STORE } from '../util/browser/idb';
import { isCentralDbEnabled } from './centralDb/config';
import { centralPost, isCentralPostPaused } from './centralDb/client';

const STORE_KEY = 'rasi-user-events-v1';
const MAX_EVENTS = 8000;

export type RasiUserEventType =
  | 'register'
  | 'login'
  | 'session_start'
  | 'logout'
  | 'message_sent'
  | 'message_edited'
  | 'message_deleted'
  | 'location_shared'
  | 'live_location_shared'
  | 'contact_shared'
  | 'photo_sent'
  | 'video_sent'
  | 'document_sent'
  | 'voice_sent'
  | 'audio_sent'
  | 'sticker_sent'
  | 'poll_sent'
  | 'chat_opened'
  | 'settings_opened'
  | 'admin_lookup'
  | 'export_data'
  | 'call_started'
  | 'call_ended'
  | 'call_active'
  | 'group_call_joined'
  | 'phone_shared'
  | 'other';

export type RasiUserEvent = {
  id: string;
  userId: string;
  type: RasiUserEventType;
  message: string;
  chatId?: string;
  messageId?: number;
  peerTitle?: string;
  meta?: Record<string, string | number | boolean | undefined>;
  timestamp: number;
};

type EventStore = { events: RasiUserEvent[] };

let memory: EventStore = { events: [] };
let loadPromise: Promise<void> | undefined;
let persistTimer: number | undefined;
let centralQueue: RasiUserEvent[] = [];
let centralFlushTimer: number | undefined;

export async function loadUserEvents(): Promise<EventStore> {
  if (!loadPromise) {
    loadPromise = (async () => {
      try {
        const stored = await MAIN_IDB_STORE.get<EventStore>(STORE_KEY);
        memory = stored?.events ? { events: stored.events.slice(-MAX_EVENTS) } : { events: [] };
      } catch {
        memory = { events: [] };
      }
    })();
  }
  await loadPromise;
  return memory;
}

function schedulePersist() {
  if (persistTimer) return;
  persistTimer = window.setTimeout(() => {
    persistTimer = undefined;
    void (async () => {
      if (memory.events.length > MAX_EVENTS) {
        memory.events = memory.events.slice(-MAX_EVENTS);
      }
      try {
        await MAIN_IDB_STORE.set(STORE_KEY, memory);
      } catch {
        // ignore
      }
    })();
  }, 2500);
}

async function flushCentralBatch() {
  if (isCentralPostPaused()) {
    centralQueue.length = 0;
    return;
  }
  const batch = centralQueue.splice(0, 8);
  await Promise.all(batch.map((entry) => centralPost('/user-events', entry)));
  if (centralQueue.length && !isCentralPostPaused()) {
    scheduleCentralFlush();
  }
}

function scheduleCentralFlush() {
  if (!isCentralDbEnabled() || centralFlushTimer) return;
  centralFlushTimer = window.setTimeout(() => {
    centralFlushTimer = undefined;
    void flushCentralBatch();
  }, 1500);
}

export async function logUserEvent(
  userId: string,
  type: RasiUserEventType,
  message: string,
  extra?: Partial<Pick<RasiUserEvent, 'chatId' | 'messageId' | 'peerTitle' | 'meta'>>,
): Promise<void> {
  if (!userId) return;
  if (!loadPromise) void loadUserEvents();
  const entry: RasiUserEvent = {
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    userId,
    type,
    message,
    timestamp: Date.now(),
    ...extra,
  };
  memory.events.push(entry);
  schedulePersist();
  if (isCentralDbEnabled()) {
    centralQueue.push(entry);
    scheduleCentralFlush();
  }
}

export function getUserEvents(userId?: string, limit = 500): RasiUserEvent[] {
  let list = memory.events;
  if (userId) list = list.filter((e) => e.userId === userId);
  return list.slice(-limit).reverse();
}

export async function mergeRemoteUserEvents(remote: RasiUserEvent[]): Promise<void> {
  if (!remote.length) return;
  await loadUserEvents();
  const map = new Map<string, RasiUserEvent>();
  memory.events.forEach((e) => map.set(e.id, e));
  remote.forEach((e) => map.set(e.id, e));
  memory.events = [...map.values()].sort((a, b) => a.timestamp - b.timestamp).slice(-MAX_EVENTS);
  schedulePersist();
}

export function classifyOutgoingMessage(message: ApiMessage): RasiUserEventType | undefined {
  if (!message.isOutgoing) return undefined;
  const c = message.content;
  if (!c) return getMessageText(message)?.text ? 'message_sent' : undefined;

  switch (c.mediaType) {
    case 'geo':
      return 'location_shared';
    case 'geoLive':
      return 'live_location_shared';
    case 'contact':
      return 'contact_shared';
    case 'photo':
      return 'photo_sent';
    case 'video':
      return 'video_sent';
    case 'document':
      return 'document_sent';
    case 'voice':
      return 'voice_sent';
    case 'audio':
      return 'audio_sent';
    case 'sticker':
      return 'sticker_sent';
    case 'poll':
      return 'poll_sent';
    default:
      return 'message_sent';
  }
}

export function describeMessageEvent(message: ApiMessage, chatTitle?: string): string {
  const type = classifyOutgoingMessage(message);
  const title = chatTitle ? ` di "${chatTitle}"` : '';
  switch (type) {
    case 'location_shared':
      return `Mengirim lokasi${title}`;
    case 'live_location_shared':
      return `Membagikan live location${title}`;
    case 'contact_shared':
      return `Membagikan kontak${title}`;
    case 'photo_sent':
      return `Mengirim foto${title}`;
    case 'video_sent':
      return `Mengirim video${title}`;
    case 'document_sent':
      return `Mengirim file${title}`;
    case 'voice_sent':
      return `Mengirim voice${title}`;
    default:
      return `Mengirim pesan${title}`;
  }
}
